import { useMutation, useQuery } from '@tanstack/react-query';
import { Switch, TabsProps } from 'antd';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { approvalConfigApi, recruitmentTypeApi } from '../../../../apis';
import {
  ApprovalConfig,
  ApprovalConfigApproveEnum,
  UpdateMultipleApprovalConfigDto,
} from '../../../../apis/client-axios';
import ButtonWrap from '../../../../components/ButtonWrap';
import TableWrap from '../../../../components/TableWrap';
import ToastError from '../../../../components/Toast/ToastError';
import ToastSuccess from '../../../../components/Toast/ToastSuccess';
import Widget from '../../../../components/Widget';
import { APPROVAL_API, QUERY_KEY } from '../../../../constants/QueryKey';
import './style.scss';

const ApplicationApproval = () => {
  const intl = useIntl();
  const [approveData, setApproveData] = useState<ApprovalConfig[]>([]);
  const [tab, setTab] = useState<string>();

  const { data: dataUserType } = useQuery({
    queryKey: [QUERY_KEY.USER_TYPE_LIST],
    queryFn: () => {
      return recruitmentTypeApi.recruitmentTypeControllerGetAll(1, undefined, undefined, undefined, undefined);
    },
  });

  useEffect(() => {
    if (Array.isArray(dataUserType?.data?.data?.content) && dataUserType?.data?.data?.content?.length) {
      setTab(dataUserType?.data?.data?.content?.[0].id);
    }
  }, [dataUserType?.data?.data?.content]);

  const { data: approvalConfigData, isLoading } = useQuery({
    queryKey: [APPROVAL_API, tab],
    queryFn: () => approvalConfigApi.approvalConfigControllerGetAllByRecruitmentTypeId(tab as string),
    enabled: !!tab,
  });

  useEffect(() => {
    setApproveData(approvalConfigData?.data?.data ?? []);
  }, [approvalConfigData]);

  const updateApprovalMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMultipleApprovalConfigDto }) =>
      approvalConfigApi.approvalConfigControllerUpdateApproveConfig(id, payload),
    onSuccess: () => {
      ToastSuccess({
        content: intl.formatMessage({ id: 'application.approval.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'application.approval.fail' }),
      });
    },
  });

  const updateApprove = () => {
    if (approveData) {
      const payload: UpdateMultipleApprovalConfigDto = {
        data: approveData,
      };
      updateApprovalMutation.mutate({ id: tab as string, payload });
    }
  };

  const onChangeSwitch = (item: ApprovalConfig, key: ApprovalConfigApproveEnum, value: boolean) => {
    const findIndex = approveData?.findIndex((x) => x.level === item.level);
    const temp = [...approveData];
    if (findIndex >= 0) {
      let tempApprove = [...temp[findIndex].approve];
      if (value && !tempApprove.includes(key)) {
        tempApprove.push(key);
      } else {
        tempApprove = tempApprove.filter((apprv) => apprv !== key);
      }
      temp[findIndex].approve = [...tempApprove];
      setApproveData(temp);
    }
  };

  const items: TabsProps['items'] = Array.isArray(dataUserType?.data?.data?.content)
    ? dataUserType?.data?.data?.content?.map((item) => ({
        key: item.id,
        label: item.type,
      }))
    : [];

  const attendanceColumns = [
    {
      title: '',
      dataIndex: 'level',
      key: 'level',
      render: (value, record) => {
        return (
          <>
            <p className="application-approval_content-data">
              {intl.formatMessage({ id: 'application.approval.column.title' }, { level: record.level })}
            </p>
          </>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.attendance.requestCheckin' }),
      dataIndex: ApprovalConfigApproveEnum.RequestCheckin,
      key: ApprovalConfigApproveEnum.RequestCheckin,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.RequestCheckin);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.RequestCheckin, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.attendance.requestCheckout' }),
      dataIndex: ApprovalConfigApproveEnum.RequestCheckout,
      key: ApprovalConfigApproveEnum.RequestCheckout,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.RequestCheckout);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.RequestCheckout, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.attendance.requestTimekeepingEdit' }),
      dataIndex: ApprovalConfigApproveEnum.RequestTimekeepingEdit,
      key: ApprovalConfigApproveEnum.RequestTimekeepingEdit,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.RequestTimekeepingEdit);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.RequestTimekeepingEdit, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.attendance.lateOn' }),
      dataIndex: ApprovalConfigApproveEnum.GoingLate,
      key: ApprovalConfigApproveEnum.GoingLate,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.GoingLate);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.GoingLate, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.attendance.leaveEarly' }),
      dataIndex: ApprovalConfigApproveEnum.LeavingSoon,
      key: ApprovalConfigApproveEnum.LeavingSoon,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.LeavingSoon);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.LeavingSoon, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.attendance.overTime' }),
      dataIndex: ApprovalConfigApproveEnum.Overtime,
      key: ApprovalConfigApproveEnum.Overtime,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.Overtime);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.Overtime, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.attendance.personalOuting' }),
      dataIndex: ApprovalConfigApproveEnum.OutsidePrivate,
      key: ApprovalConfigApproveEnum.OutsidePrivate,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.OutsidePrivate);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.OutsidePrivate, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.attendance.officialOuting' }),
      dataIndex: ApprovalConfigApproveEnum.OutsideBusiness,
      key: ApprovalConfigApproveEnum.OutsideBusiness,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.OutsideBusiness);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.OutsideBusiness, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.attendance.plannedHoliday' }),
      dataIndex: ApprovalConfigApproveEnum.RequestDayOff,
      key: ApprovalConfigApproveEnum.RequestDayOff,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.RequestDayOff);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.RequestDayOff, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.attendance.bussinessTrip' }),
      dataIndex: ApprovalConfigApproveEnum.RequestBusiness,
      key: ApprovalConfigApproveEnum.RequestBusiness,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.RequestBusiness);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.RequestBusiness, e)}
          />
        );
      },
    },
  ].map((col, _, arr) => ({ ...col, width: `${100 / arr.length}%` }));

  const scheduleColumn = [
    {
      title: '',
      dataIndex: 'schedule',
      key: 'schedule',
      render: (value, record) => {
        return <>{intl.formatMessage({ id: 'application.approval.column.title' }, { level: record.level })} </>;
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.schedule.paidHoliday' }),
      dataIndex: ApprovalConfigApproveEnum.AnnualLeave,
      key: ApprovalConfigApproveEnum.AnnualLeave,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.AnnualLeave);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.AnnualLeave, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.schedule.substituteHoliday' }),
      dataIndex: ApprovalConfigApproveEnum.CompensatoryLeave,
      key: ApprovalConfigApproveEnum.CompensatoryLeave,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.CompensatoryLeave);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.CompensatoryLeave, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.schedule.transfer' }),
      dataIndex: ApprovalConfigApproveEnum.MakeupWork,
      key: ApprovalConfigApproveEnum.MakeupWork,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.MakeupWork);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.MakeupWork, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.schedule.workOn' }),
      dataIndex: ApprovalConfigApproveEnum.WorkOnDayOff,
      key: ApprovalConfigApproveEnum.WorkOnDayOff,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.WorkOnDayOff);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.WorkOnDayOff, e)}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.schedule.bussinessTrip' }),
      dataIndex: ApprovalConfigApproveEnum.BusinessTrip,
      key: ApprovalConfigApproveEnum.BusinessTrip,
      render: (value, record, index) => {
        const isKeyInApprove = record.approve.includes(ApprovalConfigApproveEnum.BusinessTrip);
        return (
          <Switch
            defaultChecked={isKeyInApprove}
            onChange={(e) => onChangeSwitch(record, ApprovalConfigApproveEnum.BusinessTrip, e)}
          />
        );
      },
    },
  ].map((col, _, arr) => ({ ...col, width: `${100 / arr.length}%` }));

  return (
    <div className="application-approval">
      <Widget
        breadcrumbs={[intl.formatMessage({ id: 'application.approval.breadcrumbs' })]}
        tabProps={{
          items,
          onChange: (key: string) => {
            setTab(key);
          },
        }}
        content={
          <div className="application-approval_content">
            <div className="application-approval_content-attendance">
              <p className="application-approval_content-title">
                {intl.formatMessage({ id: 'application.approval.attendance' })}
              </p>
              <TableWrap
                isPagination={false}
                loading={false}
                tableProps={{
                  dataSource: approvalConfigData?.data.data || [],
                  columns: attendanceColumns,
                }}
              />
            </div>
            <div className="application-approval_content-schedule">
              <p className="application-approval_content-title">
                {intl.formatMessage({ id: 'application.approval.schedule' })}
              </p>
              <TableWrap
                isPagination={false}
                loading={isLoading}
                tableProps={{
                  dataSource: approvalConfigData?.data.data || [],
                  columns: scheduleColumn,
                }}
              />
            </div>
            <div className="application-approval_content-button">
              <ButtonWrap onClick={updateApprove} disabled={updateApprovalMutation.isPending}>
                {intl.formatMessage({ id: 'application.approval.button' })}
              </ButtonWrap>
            </div>
          </div>
        }
        title={intl.formatMessage({ id: 'application.approval.title' })}
      />
    </div>
  );
};
export default ApplicationApproval;
